import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { formatDuration } from "../utils/formatDuration";
import "./AdminDashboardStyles.css";

interface Order {
  _id: string;
  searchQuery: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  trackArtists?: string[];
  customerEmail?: string;
  clientNickname?: string | null;
  clientInstagram?: string | null;
  payoutBatchId?: string | null;
  settledAt?: string | null;
  paid?: boolean;
  originalAmount?: number | null;
  counterAmount?: number | null;
  isPriority?: boolean;
  tier?: string;
  declineReason?: string | null;
  customerName?: string | null;
  cardLast4?: string | null;
  respondedAt?: string | null;
}

interface DjUser {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  slug: string;
  instagram?: string;
  pricePerRequest?: number;
  createdAt: string;
  isFrozen?: boolean;
  freezeType?: string;
  freezeReason?: string;
  frozenUntil?: string;
}

interface DjStats {
  revenueByCurrency: Record<string, number>;
  revenue7d: number;
  orders7dCount: number;
  totalOrders: number;
}

interface PayoutEvent {
  _id: string;
  batchId: string;
  createdAt: string;
  totalGross: number;
  totalNet: number;
  ordersCount: number;
  ordersAccepted: number;
  ordersDeclined: number;
  avgRequestValue: number;
  avgResponseMs?: number | null;
}

function fmtAvatar(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

export default function AdminDjProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [dj, setDj] = useState<DjUser | null>(null);
  const [stats, setStats] = useState<DjStats | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payouts, setPayouts] = useState<PayoutEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expandedPayoutId, setExpandedPayoutId] = useState<string | null>(null);
  const [payoutOrders, setPayoutOrders] = useState<Order[]>([]);
  const [payoutOrdersLoading, setPayoutOrdersLoading] = useState(false);
  const [showTopFans, setShowTopFans] = useState(false);
  const [showUnpaid, setShowUnpaid] = useState(false);
  const [showDisputes, setShowDisputes] = useState(false);
  const [djDisputes, setDjDisputes] = useState<Array<{
    _id: string; stripeDisputeId: string; amount: number; currency: string;
    reason?: string; status: string; evidenceDueBy?: string | null;
    customerEmail?: string; customerName?: string; createdAt: string;
    order?: { _id: string; searchQuery: string; amount: number; status: string } | null;
  }>>([]);
  const [disputesLoading, setDisputesLoading] = useState(false);

  const toggleDisputes = async () => {
    if (showDisputes) { setShowDisputes(false); return; }
    setShowDisputes(true);
    if (djDisputes.length === 0 && id) {
      setDisputesLoading(true);
      try {
        const res = await adminApi.get(`/dj/${id}/disputes`);
        setDjDisputes(res.data);
      } catch { setDjDisputes([]); }
      finally { setDisputesLoading(false); }
    }
  };

  const togglePayoutExpand = async (payoutId: string) => {
    if (expandedPayoutId === payoutId) {
      setExpandedPayoutId(null);
      setPayoutOrders([]);
      return;
    }
    setExpandedPayoutId(payoutId);
    setPayoutOrdersLoading(true);
    try {
      const res = await adminApi.get(`/payouts/${payoutId}/orders`);
      setPayoutOrders(res.data);
    } catch {
      setPayoutOrders([]);
    } finally {
      setPayoutOrdersLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!deleteId) return;
    try {
      await adminApi.delete(`/orders/${deleteId}`);
      setOrders((prev) => prev.filter((o) => o._id !== deleteId));
      setDeleteId(null);
    } catch {
      alert("Failed to delete order");
    }
  };

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      const [djRes, payoutsRes] = await Promise.all([
        adminApi.get(`/dj/${id}`),
        adminApi.get(`/dj/${id}/payouts`).catch(() => ({ data: [] })),
      ]);
      setDj(djRes.data.user);
      setStats(djRes.data.stats);
      setOrders(djRes.data.recentOrders);
      setPayouts(payoutsRes.data);
    } catch {
      // error handled by showing null
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderAmountCell = (o: Order) => {
    const cur = (o.currency || "usd").toUpperCase();
    // Counter pending: DJ declined with counter, fan hasn't responded yet
    if (o.counterAmount && o.declineReason === "more_money") {
      const diff = o.counterAmount - o.amount;
      return (
        <>
          {cur} {o.counterAmount.toFixed(2)}
          <span className="amount-addon amount-counter" title={`Countered — Original: ${cur} ${o.amount.toFixed(2)}`}>+{diff.toFixed(2)}</span>
        </>
      );
    }
    // Counter accepted or boost: originalAmount preserved
    if (o.originalAmount != null && o.originalAmount !== o.amount) {
      const diff = o.amount - o.originalAmount;
      const isBoosted = !!o.isPriority;
      return (
        <>
          {cur} {o.amount.toFixed(2)}
          <span
            className={`amount-addon ${isBoosted ? "amount-boost" : "amount-counter"}`}
            title={`${isBoosted ? "Boosted" : "Countered"} — Original: ${cur} ${o.originalAmount.toFixed(2)}`}
          >
            +{diff.toFixed(2)}
          </span>
        </>
      );
    }
    // Fast pass: bid was 2x DJ minimum, show the split
    if (o.tier === "fast") {
      const half = o.amount / 2;
      return (
        <>
          {cur} {o.amount.toFixed(2)}
          <span className="amount-addon amount-fast" title={`FastPass — base ${cur} ${half.toFixed(2)} + ${cur} ${half.toFixed(2)} premium`}>+{half.toFixed(2)}</span>
        </>
      );
    }
    return <>{cur} {o.amount.toFixed(2)}</>;
  };

  const renderStatusCell = (o: Order, size: "sm" | "xs" = "sm") => {
    if (o.status === "declined" && o.declineReason) {
      const reason = o.declineReason === "pas_ambience" ? "Not the Vibe"
        : o.declineReason === "no_access" ? "No Access to Song"
        : o.declineReason === "more_money" ? "Counter Offered"
        : o.declineReason;
      return (
        <span className={`badge badge-${size} badge-declined`} title={reason} style={{ cursor: "help" }}>
          declined *
        </span>
      );
    }
    return <span className={`badge badge-${size} badge-${o.status || "pending"}`}>{o.status}</span>;
  };

  // Top fans: group by customer identifier, filter count > 1
  const topFans = useMemo(() => {
    const map = new Map<string, {
      key: string;
      email: string | null;
      name: string | null;
      nickname: string | null;
      allNicknames: Set<string>;
      instagram: string | null;
      cardLast4: string | null;
      count: number;
      totalSpent: number;
      lastRequest: string;
    }>();

    for (const o of orders) {
      const identifier = o.customerEmail?.toLowerCase().trim()
        || o.clientInstagram?.toLowerCase().trim()
        || o.clientNickname?.trim()
        || null;
      if (!identifier) continue;

      const existing = map.get(identifier);
      if (existing) {
        existing.count++;
        existing.totalSpent += o.amount || 0;
        if (o.clientNickname) existing.allNicknames.add(o.clientNickname);
        if (o.createdAt > existing.lastRequest) {
          existing.lastRequest = o.createdAt;
          if (o.clientNickname) existing.nickname = o.clientNickname;
        }
        if (!existing.name && o.customerName) existing.name = o.customerName;
        if (!existing.email && o.customerEmail) existing.email = o.customerEmail;
        if (!existing.instagram && o.clientInstagram) existing.instagram = o.clientInstagram;
        if (!existing.cardLast4 && o.cardLast4) existing.cardLast4 = o.cardLast4;
      } else {
        const nickSet = new Set<string>();
        if (o.clientNickname) nickSet.add(o.clientNickname);
        map.set(identifier, {
          key: identifier,
          email: o.customerEmail || null,
          name: o.customerName || null,
          nickname: o.clientNickname || null,
          allNicknames: nickSet,
          instagram: o.clientInstagram || null,
          cardLast4: o.cardLast4 || null,
          count: 1,
          totalSpent: o.amount || 0,
          lastRequest: o.createdAt,
        });
      }
    }

    return Array.from(map.values())
      .filter((f) => f.count > 1)
      .sort((a, b) => b.count - a.count || b.totalSpent - a.totalSpent);
  }, [orders]);

  // Average response time across all orders with respondedAt
  const avgResponseTime = useMemo(() => {
    const responded = orders.filter((o) => o.respondedAt && o.createdAt);
    if (responded.length === 0) return { ms: null, count: 0 };
    const totalMs = responded.reduce((acc, o) => {
      return acc + (new Date(o.respondedAt!).getTime() - new Date(o.createdAt).getTime());
    }, 0);
    return { ms: totalMs / responded.length, count: responded.length };
  }, [orders]);

  if (loading) {
    return (
      <div id="adm-root-light" style={{ alignItems: "center", justifyContent: "center" }}>
        <p className="muted-text">Loading...</p>
      </div>
    );
  }

  if (!dj) {
    return (
      <div id="adm-root-light" style={{ alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <h3>Talent Not Found</h3>
        <button className="adm-btn-light" onClick={() => navigate("/admin/dashboard")}>← Back</button>
      </div>
    );
  }

  const avatarSrc = fmtAvatar(dj.avatarUrl);
  const revenueEntries = stats ? Object.entries(stats.revenueByCurrency) : [];

  return (
    <div id="adm-root-light">
      <main id="adm-main-light">
        {/* Header */}
        <header id="adm-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="adm-btn-light" onClick={() => navigate("/admin/dashboard")}>
              ← Back
            </button>
            <div>
              <h1 className="adm-heading" style={{ fontSize: "1.25rem" }}>
                {dj.name}
              </h1>
              {dj.isFrozen && (
                <span className="badge badge-sm badge-frozen" style={{ marginTop: 2 }}>
                  {dj.freezeType === "permanent" ? "Permanently Frozen" : "Temporarily Frozen"}
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="adm-content-area">
          {/* Profile + Stats row */}
          <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 16, marginBottom: 16 }}>
            {/* Profile card */}
            <div className="adm-card" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 8 }}>
              {avatarSrc ? (
                <img src={avatarSrc} alt={dj.name} style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", border: "3px solid #f3f4f6" }} />
              ) : (
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, fontWeight: 800, color: "#9ca3af" }}>
                  {dj.name[0].toUpperCase()}
                </div>
              )}
              <div style={{ fontWeight: 800, fontSize: "1rem" }}>{dj.name}</div>
              <div className="pill">@{dj.slug}</div>
              <div style={{ borderTop: "1px solid var(--adm-line)", paddingTop: 10, width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
                <div className="muted-text" style={{ fontSize: "0.8rem" }}>✉ {dj.email}</div>
                {dj.instagram && (
                  <div style={{ fontSize: "0.8rem" }}>
                    <a href={`https://instagram.com/${dj.instagram}`} target="_blank" rel="noreferrer" style={{ color: "#E1306C", textDecoration: "none" }}>
                      📷 @{dj.instagram}
                    </a>
                  </div>
                )}
                <div className="muted-text" style={{ fontSize: "0.78rem" }}>
                  Joined {new Date(dj.createdAt).toLocaleDateString()}
                </div>
                <div style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  Price: {dj.pricePerRequest ? `$${dj.pricePerRequest}` : "Free"}
                </div>
              </div>
            </div>

            {/* KPI stats */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignContent: "start" }}>
              <div className="kpi-card" style={{ cursor: "default" }}>
                <div className="kpi-icon green-bg">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                    <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                  </svg>
                </div>
                <div>
                  <div className="kpi-label">Lifetime Revenue</div>
                  <div className="kpi-value-row">
                    {revenueEntries.length > 0
                      ? revenueEntries.map(([cur, amt]) => (
                          <span key={cur}>{cur.toUpperCase()} {amt.toFixed(0)}</span>
                        ))
                      : <span>$0</span>}
                  </div>
                </div>
              </div>

              <div className="kpi-card" style={{ cursor: "default" }}>
                <div className="kpi-icon blue-bg">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <div>
                  <div className="kpi-label">Total Orders</div>
                  <div className="kpi-value">{stats?.totalOrders ?? 0}</div>
                </div>
              </div>

              <div className="kpi-card" style={{ cursor: "default" }}>
                <div className="kpi-icon orange-bg">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                </div>
                <div>
                  <div className="kpi-label">Revenue (7d)</div>
                  <div className="kpi-value">${stats?.revenue7d.toFixed(2) ?? "0.00"}</div>
                </div>
              </div>

              <div className="kpi-card" style={{ cursor: "default" }}>
                <div className="kpi-icon purple-bg">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                    <polyline points="17 6 23 6 23 12"/>
                  </svg>
                </div>
                <div>
                  <div className="kpi-label">Orders (7d)</div>
                  <div className="kpi-value">{stats?.orders7dCount ?? 0}</div>
                </div>
              </div>

              <div className="kpi-card" style={{ cursor: "default" }}>
                <div className="kpi-icon red-bg">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--adm-red)" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div>
                  <div className="kpi-label">Avg. Response Time</div>
                  <div className="kpi-value">{avgResponseTime.ms != null ? formatDuration(avgResponseTime.ms) : "N/A"}</div>
                  {avgResponseTime.count > 0 && (
                    <div className="kpi-sublabel">{avgResponseTime.count} responses</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payout History */}
          {payouts.length > 0 && (
            <div className="adm-card" style={{ marginBottom: 16 }}>
              <h2 className="adm-section-title" style={{ marginBottom: 12 }}>Payout History</h2>
              <div className="adm-table-wrapper">
                <table className="adm-table-light adm-table-compact sticky-header">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Gross</th>
                      <th>Net (Talent)</th>
                      <th>Orders</th>
                      <th>Accepted</th>
                      <th>Declined</th>
                      <th>Avg. Value</th>
                      <th>Avg. Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.map((p) => {
                      const isExpanded = expandedPayoutId === p._id;
                      return (
                        <React.Fragment key={p._id}>
                          <tr
                            className="hover-row clickable"
                            onClick={() => togglePayoutExpand(p._id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <span style={{ marginRight: 6, display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", fontSize: "0.7rem" }}>
                                ▶
                              </span>
                              {new Date(p.createdAt).toLocaleDateString()}
                            </td>
                            <td className="muted-text">{new Date(p.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                            <td style={{ fontWeight: 600 }}>${p.totalGross.toFixed(2)}</td>
                            <td style={{ fontWeight: 700, color: "var(--adm-green)" }}>${p.totalNet.toFixed(2)}</td>
                            <td>{p.ordersCount}</td>
                            <td style={{ color: "var(--adm-green)", fontWeight: 600 }}>{p.ordersAccepted}</td>
                            <td style={{ color: "var(--adm-red)" }}>{p.ordersDeclined}</td>
                            <td>${p.avgRequestValue.toFixed(2)}</td>
                            <td className="muted-text" style={{ fontSize: "0.78rem" }}>
                              {p.avgResponseMs != null ? formatDuration(p.avgResponseMs) : "—"}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={9} style={{ padding: 0, background: "#f9fafb" }}>
                                <div className="payout-expand-panel">
                                  {payoutOrdersLoading ? (
                                    <p className="muted-text" style={{ padding: "12px 16px", margin: 0, fontSize: "0.8rem" }}>Loading orders...</p>
                                  ) : payoutOrders.length === 0 ? (
                                    <p className="muted-text" style={{ padding: "12px 16px", margin: 0, fontSize: "0.8rem" }}>No orders found for this cashout.</p>
                                  ) : (
                                    <table className="adm-table-light adm-table-xs" style={{ width: "100%", background: "#f9fafb" }}>
                                      <thead>
                                        <tr>
                                          <th style={{ width: "38%" }}>Track</th>
                                          <th style={{ width: "14%" }}>Amount</th>
                                          <th style={{ width: "10%" }}>Status</th>
                                          <th style={{ width: "18%" }}>Client</th>
                                          <th style={{ width: "20%" }}>Date</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {payoutOrders.map((o) => (
                                          <tr key={o._id}>
                                            <td style={{ overflow: "hidden", maxWidth: 0 }}>
                                              <span className="truncate-inline" title={o.searchQuery}>
                                                {o.searchQuery}
                                              </span>
                                            </td>
                                            <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                              {renderAmountCell(o)}
                                            </td>
                                            <td>
                                              {renderStatusCell(o, "xs")}
                                            </td>
                                            <td style={{ overflow: "hidden", maxWidth: 0 }}>
                                              <span
                                                className="truncate-inline"
                                                style={{ fontSize: "0.72rem" }}
                                                title={[o.customerName, o.clientInstagram ? `@${o.clientInstagram}` : null, o.customerEmail, o.clientNickname, o.cardLast4 ? `•••• ${o.cardLast4}` : null].filter(Boolean).join(" | ") || undefined}
                                              >
                                                {o.customerName || (o.clientInstagram ? `@${o.clientInstagram}` : null) || o.customerEmail || o.clientNickname || (o.cardLast4 ? `•••• ${o.cardLast4}` : null) || "—"}
                                              </span>
                                            </td>
                                            <td className="muted-text" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                              {new Date(o.createdAt).toLocaleDateString()}{" "}
                                              {new Date(o.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Fans */}
          {topFans.length > 0 && (
            <div className="adm-card" style={{ marginBottom: 16 }}>
              <div
                className="card-header-flex"
                style={{ cursor: "pointer", marginBottom: showTopFans ? 12 : 0 }}
                onClick={() => setShowTopFans((v) => !v)}
              >
                <h2 className="adm-section-title">
                  <span style={{ display: "inline-block", transition: "transform 0.2s", transform: showTopFans ? "rotate(90deg)" : "rotate(0deg)", fontSize: "0.7rem", marginRight: 8 }}>▶</span>
                  Top Fans ({topFans.length})
                </h2>
              </div>
              {showTopFans && <div className="adm-table-wrapper">
                <table className="adm-table-light adm-table-compact">
                  <thead>
                    <tr>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "22%" }}>Fan</th>
                      <th style={{ width: "22%" }}>Email</th>
                      <th style={{ width: "17%" }}>Instagram / Nickname</th>
                      <th style={{ width: "10%" }}>Requests</th>
                      <th style={{ width: "12%" }}>Total Spent</th>
                      <th style={{ width: "12%" }}>Last Request</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topFans.map((f, i) => (
                      <tr key={f.key} className="hover-row">
                        <td className="muted-text" style={{ fontWeight: 600 }}>{i + 1}</td>
                        <td
                          style={{ fontWeight: 600, fontSize: "0.82rem" }}
                          title={[f.cardLast4 ? `•••• ${f.cardLast4}` : null].filter(Boolean).join("") || undefined}
                        >
                          {f.name || f.key}
                        </td>
                        <td style={{ overflow: "hidden", maxWidth: 0 }}>
                          <span
                            className="muted-text"
                            style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.76rem" }}
                          >
                            {f.email || "—"}
                          </span>
                        </td>
                        <td style={{ fontSize: "0.78rem" }}>
                          {(() => {
                            const others = Array.from(f.allNicknames).filter((n) => n !== (f.instagram || f.nickname));
                            const hoverText = others.length > 0 ? `Previous: ${others.join(", ")}` : undefined;
                            if (f.instagram) {
                              return (
                                <a href={`https://instagram.com/${f.instagram}`} target="_blank" rel="noreferrer" style={{ color: "#E1306C", textDecoration: "none" }} title={hoverText}>
                                  @{f.instagram}
                                </a>
                              );
                            }
                            if (f.nickname) {
                              return <span title={hoverText}>{f.nickname}</span>;
                            }
                            return <span className="muted-text">—</span>;
                          })()}
                        </td>
                        <td style={{ fontWeight: 700 }}>{f.count}</td>
                        <td style={{ fontWeight: 600, color: "var(--adm-green)" }}>
                          ${f.totalSpent.toFixed(2)}
                        </td>
                        <td className="muted-text" style={{ fontSize: "0.76rem", whiteSpace: "nowrap" }}>
                          {new Date(f.lastRequest).toLocaleDateString()}{" "}
                          {new Date(f.lastRequest).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
            </div>
          )}

          {/* Disputes */}
          <div className="adm-card" style={{ marginBottom: 16 }}>
            <div
              className="card-header-flex"
              style={{ cursor: "pointer", marginBottom: showDisputes ? 12 : 0 }}
              onClick={toggleDisputes}
            >
              <h2 className="adm-section-title">
                <span style={{ display: "inline-block", transition: "transform 0.2s", transform: showDisputes ? "rotate(90deg)" : "rotate(0deg)", fontSize: "0.7rem", marginRight: 8 }}>▶</span>
                Disputes {djDisputes.length > 0 ? `(${djDisputes.length})` : ""}
              </h2>
            </div>
            {showDisputes && (
              <div>
                {disputesLoading ? (
                  <p className="muted-text" style={{ padding: "8px 0", fontSize: "0.8rem" }}>Loading disputes...</p>
                ) : djDisputes.length === 0 ? (
                  <p className="muted-text" style={{ padding: "8px 0", fontSize: "0.8rem" }}>No disputes for this talent.</p>
                ) : (
                  <div className="adm-table-wrapper">
                    <table className="adm-table-light adm-table-compact">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Amount</th>
                          <th>Reason</th>
                          <th>Client</th>
                          <th>Evidence Due</th>
                          <th>Order</th>
                        </tr>
                      </thead>
                      <tbody>
                        {djDisputes.map((d) => {
                          const isOverdue = d.evidenceDueBy && new Date(d.evidenceDueBy) <= new Date();
                          const statusLabel = d.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                          const statusClass = d.status.includes("won") ? "badge-accepted"
                            : d.status.includes("review") ? "badge-queued"
                            : "badge-declined";
                          return (
                            <tr key={d._id} className="hover-row">
                              <td style={{ whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                                {new Date(d.createdAt).toLocaleDateString()}
                              </td>
                              <td><span className={`badge badge-sm ${statusClass}`}>{statusLabel}</span></td>
                              <td style={{ fontWeight: 700 }}>${(d.amount / 100).toFixed(2)}</td>
                              <td style={{ fontSize: "0.78rem" }}>
                                {d.reason ? d.reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—"}
                              </td>
                              <td style={{ fontSize: "0.78rem" }}>
                                {d.customerName || d.customerEmail || "—"}
                              </td>
                              <td style={{ fontSize: "0.78rem" }}>
                                {d.evidenceDueBy ? (
                                  <span style={{ color: isOverdue ? "var(--adm-red)" : "var(--adm-orange)", fontWeight: isOverdue ? 700 : undefined }}>
                                    {new Date(d.evidenceDueBy).toLocaleDateString()}
                                    {isOverdue && " (overdue)"}
                                  </span>
                                ) : "—"}
                              </td>
                              <td style={{ fontSize: "0.76rem" }} className="muted-text">
                                {d.order?.searchQuery?.slice(0, 20) || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected (unpaid) */}
          {(() => {
            const unpaid = orders.filter((o) => !o.paid);
            if (unpaid.length === 0) return null;
            return (
              <div className="adm-card">
                <div
                  className="card-header-flex"
                  style={{ cursor: "pointer", marginBottom: showUnpaid ? 12 : 0 }}
                  onClick={() => setShowUnpaid((v) => !v)}
                >
                  <h2 className="adm-section-title">
                    <span style={{ display: "inline-block", transition: "transform 0.2s", transform: showUnpaid ? "rotate(90deg)" : "rotate(0deg)", fontSize: "0.7rem", marginRight: 8 }}>▶</span>
                    Selected (unpaid) ({unpaid.length})
                  </h2>
                </div>
                {showUnpaid && (
                <div className="adm-scrollable-full" style={{ maxHeight: 460 }}>
                  <table className="adm-table-light adm-table-compact sticky-header" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ width: "44%" }}>Track</th>
                        <th style={{ width: "12%" }}>Amount</th>
                        <th style={{ width: "18%" }}>Client</th>
                        <th style={{ width: "16%" }}>Date</th>
                        <th style={{ width: "10%" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {unpaid.map((o) => (
                        <tr key={o._id} className="hover-row">
                          <td style={{ overflow: "hidden", maxWidth: 0 }}>
                            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.searchQuery}>
                              {o.searchQuery}
                            </span>
                          </td>
                          <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                            {(o.currency || "usd").toUpperCase()} {o.amount.toFixed(2)}
                          </td>
                          <td style={{ overflow: "hidden", maxWidth: 0 }}>
                            <span
                              style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.76rem" }}
                              title={[o.customerName, o.clientInstagram ? `@${o.clientInstagram}` : null, o.customerEmail, o.clientNickname, o.cardLast4 ? `•••• ${o.cardLast4}` : null].filter(Boolean).join(" | ") || undefined}
                            >
                              {o.customerName || (o.clientInstagram ? `@${o.clientInstagram}` : null) || o.customerEmail || o.clientNickname || (o.cardLast4 ? `•••• ${o.cardLast4}` : null) || "—"}
                            </span>
                          </td>
                          <td className="muted-text" style={{ fontSize: "0.76rem", whiteSpace: "nowrap" }}>
                            {new Date(o.createdAt).toLocaleDateString()}{" "}
                            {new Date(o.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td>
                            <button
                              className="adm-btn-sm adm-btn-red-sm"
                              onClick={() => setDeleteId(o._id)}
                              title="Delete order"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {unpaid.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>
                            No unpaid selections
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Delete confirmation modal */}
        {deleteId && (
          <div className="adm-modal-backdrop" onClick={() => setDeleteId(null)}>
            <div className="adm-modal-box" onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: "0 0 8px" }}>Delete Order</h3>
              <p style={{ color: "#6b7280", fontSize: "0.88rem", margin: "0 0 16px" }}>
                Are you sure you want to permanently delete this order? This action cannot be undone.
              </p>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="adm-btn-light" onClick={() => setDeleteId(null)}>Cancel</button>
                <button className="adm-btn-sm adm-btn-red-sm" onClick={handleDeleteOrder}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
